import { Popover } from 'antd';
import moment from 'moment';
import { ReactNode } from "react";
import { renderErrorMessage } from "../../../../../components/messages/errorMessage";
import StatusTag from '../../../../../components/statusTag';
import _t from "../../../../../lang/translate";
import { getTagsAutoComplete } from "../../../../../services/autocompleteService";
import { getGovernmentCaseHandler } from '../../../../../services/taxLegalService';
import { EnumItemType } from '../../../../../types/appTypes';
import { LooseAutocomplete } from "../../../../../types/utilTypes";
import format from "../../../../../utilities/formatNumbers";
import { isString, isValidNum } from "../../../../../utilities/typeGuard";
import { AllFieldKeys } from "./types";

function getCustomerFormatter(key: LooseAutocomplete<AllFieldKeys>): null | ((value: unknown) => string | number | ReactNode | Promise<string | number | ReactNode>) {

  switch (key) {
    case "allDMRDocumentsRequestedAt": return (value: unknown) => {
      if (typeof value === 'boolean') {
        return format.yesOrNo(value)
      }
      return _t("empty")
    }
    case "analysisTags": return async (value: unknown) => {
      if (Array.isArray(value) && value.length) {
        try {
          const { data: { data: tags } } = await getTagsAutoComplete("tax_legal_analysis")
          return (
            <div style={{ display: "flex", gap: 4, flexWrap: 'wrap' }
            }> {
                value.map((item) => {
                  const tag = tags?.find(tag => parseInt(item) === tag.id)
                  return <span key={String(item)} className='font-smallest text-nowrap text-medium bg-muted rounded-16 pl-05 pr-05 pt-01 pb-01'>{tag?.title ?? item}</span>
                })
              }
            </div>
          )
        } catch (error) { renderErrorMessage(error) }
      }
      return _t("empty")
    }
    case "complianceTags":
    case "lackOfComplianceTags": return async (value: unknown) => {
      if (Array.isArray(value) && value.length) {
        try {
          const { data: { data: tags } } = await getTagsAutoComplete("tax_legal_compliance")
          return (
            <div style={{ display: "flex", gap: 4, flexWrap: 'wrap' }
            }> {
                value.map((item) => {
                  const tag = tags?.find(tag => parseInt(item) === tag.id)
                  return <span key={String(item)} className='font-smallest text-nowrap text-medium bg-muted rounded-16 pl-05 pr-05 pt-01 pb-01'>{tag?.title ?? item} </span>
                })
              }
            </div>
          )
        } catch (error) { renderErrorMessage(error) }
      }
      return _t("empty")
    }
    case "penaltyType":
    case "status": return async (value: unknown) => {
      if (!!value && typeof value === 'object' && 'title' in value && 'id' in value) {
        // font-smallest text-nowrap text-medium bg-muted rounded-16 pl-05 pr-05 pt-01 pb-01
        return <StatusTag style={{ fontSize: 11, lineHeight: 1.5, padding: "0.1rem 0.5rem" }} size="small" status={value as EnumItemType} />
      }
      return _t("empty")
    }
    case "governmentCaseHandlerId": return async (value: unknown) => {
      if (isValidNum(value)) {
        try {
          const { data: caseHandler } = await getGovernmentCaseHandler(value)
          return (
            <span className=''>{caseHandler.name} <span style={{ fontSize: 9 }}>({caseHandler.type?.title})</span></span>
          )
        } catch (error) { renderErrorMessage(error) }
      }
      return _t("empty")
    }
    case "activeStage": return (value: unknown) => {
      if (value && isString(value)) {
        return _t(value as "price_check" | "complaint" | "resumption")
      }
      return _t("empty")
    }
    case "governmentCaseNumber": return (value: unknown) => {
      if (value && isString(value)) {
        const caseNumbers = value.split(',')
        const last = caseNumbers.pop()

        if (caseNumbers.length) {
          return (
            <Popover title={null} content={(
              <div>
                {caseNumbers.map((caseNumber, i) => <div key={i}>{caseNumber}</div>)}
              </div>
            )}>
              <span style={{ textDecoration: "underline" }}>
                {last}
              </span>
            </Popover>
          )
        }

        return value
      }
      return _t("empty")
    }
    default: return null;
  }
}
export async function formatValue(key: LooseAutocomplete<AllFieldKeys>, value: unknown) {
  const customFormatter = getCustomerFormatter(key);
  if (customFormatter && typeof customFormatter === 'function') return await customFormatter(value)
  if (isValidNum(value)) return format.number(value, { maximumFractionDigits: 0 });
  if (typeof value === 'string') {
    if (moment(value).isValid()) {
      return format.date(value)
    }
    return value
  }
  if (typeof value === 'boolean') {
    return format.yesOrNo(value)
  }
  return _t("empty")
}
